import React from 'react';
import Select, {SingleValue} from 'react-select';
import {getActionSelectStyles} from '../../utility/selectStyles';
import {FreeVm, FreeVmOption, FreeVmOptionGroup, FreeVmStatus, freeVmColumns, getFreeVmActions} from './freeVm';

/**
 * A free VM in the instances table (FreeVmRow) and in its mobile cards (FreeVmCard): the same columns, badges and
 * Actions select as a paid row (InstanceRow), from a FreeVm instead of an Instance.
 *
 * A click opens the free VM's details in the paid details view's look (FreeVmDetailsModal), never the paid details
 * page: that reads a paid instance by its id (its volumes, action log and routes), which a free VM does not have, so it
 * would land on "Instance not found" (Milos, 2026-11-02: "Just use a previous one that was for normal VMs").
 */
interface FreeVmRowProps {
    vm: FreeVm;
    /** Its Delete request is in flight. */
    deleting: boolean;
    onDelete: (vm: FreeVm) => void;
    onViewData: (vm: FreeVm) => void;
    /** Opens its details. */
    onOpen: (vm: FreeVm) => void;
}

// One badge: the Image column already says which VM is the PostgreSQL one.
const FreeVmBadges: React.FC<{vm: FreeVm}> = () => (
    <span className="cluster-badge free-tier-badge" title="Included free VM. Your agent manages it through redu MCP.">Free tier</span>
);

/** A paid row's status markup: the progress bar for the same phase, or the plain label. */
const FreeVmStatusCell: React.FC<{status: FreeVmStatus; card?: boolean}> = ({status, card}) => {
    if (!status.progress) return card ? <span className="value">{status.label}</span> : <>{status.label}</>;
    const Label = card ? 'span' : 'div';
    return (
        <div>
            <div className={status.progress === 'deleting' ? 'deleting-progress' : 'building-progress'}></div>
            <Label className="status-label" style={{color: status.progress === 'deleting' ? '#ef4444' : 'var(--status-info)'}}>{status.label}</Label>
        </div>
    );
};

// The free VM menu carries group headings and reasons a paid row's menu does not, so it is wider than its button,
// and its right edge stays on the button's so it never runs off the screen in the last column.
const FREE_VM_MENU_WIDTH = 300;
const actionStyles = getActionSelectStyles();
const freeVmActionStyles = {
    ...actionStyles,
    menuPortal: (base: any) => {
        const width = Math.max(Number(base.width) || 0, FREE_VM_MENU_WIDTH);
        return {...actionStyles.menuPortal(base), width, left: (Number(base.left) || 0) + (Number(base.width) || 0) - width};
    },
};

const FreeVmActions: React.FC<FreeVmRowProps> = ({vm, deleting, onDelete, onViewData}) => (
    <Select<FreeVmOption, false, FreeVmOptionGroup>
        aria-label={`Actions for ${vm.name}`}
        options={getFreeVmActions(vm, deleting)}
        // A menu of commands, not a choice to keep: it always reads "Actions", so a cancelled Delete is not shown as chosen.
        value={null}
        onChange={(option: SingleValue<FreeVmOption>) => {
            if (option?.value === 'delete') onDelete(vm);
            else if (option?.value === 'view_data' && vm.databaseData?.data_view === true) onViewData(vm);
        }}
        placeholder="Actions"
        isSearchable={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={freeVmActionStyles}
    />
);

export const FreeVmRow: React.FC<FreeVmRowProps> = (props) => {
    const {vm} = props;
    const columns = freeVmColumns(vm);
    return (
        <tr className="free-tier-row clickable-row" onClick={() => props.onOpen(vm)}>
            <td>
                <div className="instance-name-cell">
                    <span>{vm.name}</span>
                    <FreeVmBadges vm={vm}/>
                </div>
            </td>
            <td><FreeVmStatusCell status={columns.status}/></td>
            <td title={columns.imageFull}>{columns.image}</td>
            <td title={columns.flavorFull}>{columns.flavor}</td>
            <td>{columns.created}</td>
            <td>
                <div className="updated-date">{columns.updated}</div>
            </td>
            <td>{columns.uptime}</td>
            <td className="free-tier-row__muted">{columns.state}</td>
            <td className="free-tier-row__muted">{columns.task}</td>
            <td onClick={(e) => e.stopPropagation()}><FreeVmActions {...props}/></td>
        </tr>
    );
};

export const FreeVmCard: React.FC<FreeVmRowProps> = (props) => {
    const {vm} = props;
    const columns = freeVmColumns(vm);
    const fields: Array<[string, string]> = [
        ['Image', columns.imageFull], ['Flavor', columns.flavorFull], ['Created', columns.created], ['Updated', columns.updated],
        ['Uptime', columns.uptime], ['State', columns.state], ['Task', columns.task],
    ];
    return (
        <div className="keypair-card free-tier-card" onClick={() => props.onOpen(vm)}>
            <div className="card-header">
                <div className="card-title-row">
                    <h3 className="card-title">{vm.name}</h3>
                </div>
                <div className="card-badge-row"><FreeVmBadges vm={vm}/></div>
                <div style={{width: '100%', marginTop: '12px'}} onClick={(e) => e.stopPropagation()}>
                    <FreeVmActions {...props}/>
                </div>
            </div>
            <div className="card-content">
                <div className="fingerprint">
                    <span className="label">Status:</span>
                    <FreeVmStatusCell status={columns.status} card/>
                </div>
                {fields.map(([label, value]) => (
                    <div className="fingerprint" key={label}>
                        <span className="label">{label}:</span>
                        <span className="value">{value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
